import { useState, useEffect, useCallback } from 'react';
import { imageHistoryService } from '../services/imageHistoryService';
import {
  ImageHistoryEntry,
  GeneratedImage,
  ImageGenerationSettings,
} from '../types/imageGeneration';

/**
 * Hook for managing image generation history
 */
const IMAGE_HISTORY_UPDATED_EVENT = 'image_history_updated';

export const useImageHistory = () => {
  const [history, setHistory] = useState<ImageHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      let entries = await imageHistoryService.loadImageHistory();
      entries = entries.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      setHistory(entries);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load history');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load history on mount
  useEffect(() => {
    loadHistory();
    const handleExternalUpdate = () => {
      // Always refetch from source to ensure fresh data (server or local fallback)
      loadHistory();
    };
    window.addEventListener(IMAGE_HISTORY_UPDATED_EVENT, handleExternalUpdate);
    return () => {
      window.removeEventListener(IMAGE_HISTORY_UPDATED_EVENT, handleExternalUpdate);
    };
  }, [loadHistory]);

  const saveGeneration = useCallback(
    async (
      prompt: string,
      images: GeneratedImage[],
      settings: ImageGenerationSettings
    ): Promise<ImageHistoryEntry | null> => {
      setError(null);
      try {
        const entry = await imageHistoryService.saveImageGeneration(
          prompt,
          images,
          settings
        );
        // Append locally for immediate UX while also broadcasting update
        setHistory(prev => [...prev, entry]);
        // Trigger global refresh for all listeners and fetch latest (server truth if available)
        try {
          window.dispatchEvent(new Event(IMAGE_HISTORY_UPDATED_EVENT));
        } catch (_) {}
        // Also refresh this instance to sync with possible server-transformed data
        loadHistory();
        return entry;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save generation');
        return null;
      }
    },
    [loadHistory]
  );

  const deleteEntry = useCallback(async (id: string): Promise<boolean> => {
    setError(null);
    try {
      await imageHistoryService.deleteHistoryEntry(id);
      setHistory(prev => prev.filter(entry => entry.id !== id));
      try {
        window.dispatchEvent(new Event(IMAGE_HISTORY_UPDATED_EVENT));
      } catch (_) {}
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete entry');
      return false;
    }
  }, []);

  const clearAllHistory = useCallback(async (): Promise<boolean> => {
    setError(null);
    try {
      await imageHistoryService.clearHistory();
      setHistory([]);
      try {
        window.dispatchEvent(new Event(IMAGE_HISTORY_UPDATED_EVENT));
      } catch (_) {}
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear history');
      return false;
    }
  }, []);

  const getEntry = useCallback(
    async (id: string): Promise<ImageHistoryEntry | null> => {
      setError(null);
      try {
        return await imageHistoryService.getHistoryEntry(id);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to get entry');
        return null;
      }
    },
    []
  );

  return {
    history,
    isLoading,
    error,
    loadHistory,
    saveGeneration,
    deleteEntry,
    clearAllHistory,
    getEntry,
  };
};